import { Box, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { CitationReference } from "../../../../../api/types/workflowTypes";
import type { PdfHighlight } from "../../../../common/document-preview/PdfHighlightsPreview";
import PdfHighlightsPreview from "../../../../common/document-preview/PdfHighlightsPreview";

interface Props {
  workflowId: string;
  documentName: string;
  citations: CitationReference[];
  selectedCitationId?: string;
}

const PdfCitationsPreview = ({ workflowId, documentName, citations, selectedCitationId }: Props) => {
  const allDeleted =
    citations.length > 0 && citations.every((c) => c.highlightResolution?.status === "DocumentDeleted");

  if (allDeleted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
        <Typography color="textSecondary">Source removed — the original file was deleted by a user.</Typography>
      </Box>
    );
  }

  const documentDataCatalogId = citations.find((c) => c.documentDataCatalogId)?.documentDataCatalogId;

  if (!documentDataCatalogId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
        <Typography color="textSecondary">Document preview not available</Typography>
      </Box>
    );
  }

  return (
    <PdfCitationsContent
      workflowId={workflowId}
      documentName={documentName}
      documentDataCatalogId={documentDataCatalogId}
      citations={citations}
      selectedCitationId={selectedCitationId}
    />
  );
};

interface ContentProps {
  workflowId: string;
  documentName: string;
  documentDataCatalogId: string;
  citations: CitationReference[];
  selectedCitationId?: string;
}

const PdfCitationsContent = ({
  workflowId,
  documentName,
  documentDataCatalogId,
  citations,
  selectedCitationId,
}: ContentProps) => {
  const getFileDownloadInfo = useCallback(
    () => adminApi.getWorkflowDocumentFileDownloadInfo(workflowId, documentDataCatalogId),
    [workflowId, documentDataCatalogId]
  );

  const [fileInfo, fetchError] = useFetch(getFileDownloadInfo);

  const highlights = useMemo<PdfHighlight[]>(() => {
    const result: PdfHighlight[] = [];
    for (const citation of citations) {
      if (!citation.id || citation.highlightResolution?.status !== "Resolved") {
        continue;
      }

      const rects = citation.highlightResolution.occurrences?.[0] ?? citation.highlightResolution.highlights;
      if (!rects) {
        continue;
      }

      for (const rect of rects) {
        result.push({
          citationId: citation.id,
          pageNumber: rect.pageNumber,
          leftPct: rect.leftPct,
          topPct: rect.topPct,
          widthPct: rect.widthPct,
          heightPct: rect.heightPct,
        });
      }
    }
    return result;
  }, [citations]);

  if (fetchError) {
    logError(fetchError, "[PdfCitationsPreview] getWorkflowDocumentFileDownloadInfo");
    return <DataLoadingFailed title="Could not load document" />;
  }

  if (fileInfo === undefined) {
    return <InlineLoader />;
  }

  return (
    <PdfHighlightsPreview
      fileSrc={fileInfo.downloadUrl}
      fileName={documentName}
      highlights={highlights}
      selectedCitationId={selectedCitationId}
      autoScrollToCitationId={selectedCitationId}
    />
  );
};

export default PdfCitationsPreview;
